import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import HotKeyListener from '@/components/ux/hot-key-listener';
import { toast } from '@/hooks/use-toast';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { cn } from '@/lib/utils';
import { vehicleListingStore } from '@/models/stores/vehicle-listings';
import type { VehicleListing } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { Key } from 'lucide-react';
import { useMemo, useRef } from 'react';
import {
  ComboboxPopover,
  type Option,
} from '../../ui/combobox/combobox-popover';
import { VehicleNumberOfKeysBadge } from './number-of-keys-badge';

export const getNumberOfKeysOptions = (): Option<string>[] => {
  const keyIcon = <Key className="h-3 w-3" />;
  return [
    {
      value: '0',
      label: 'None',
      icon: keyIcon,
      hotkey: '0',
    },
    {
      value: '1',
      label: '1 key',
      icon: keyIcon,
      hotkey: '1',
    },
    {
      value: '2',
      label: '2 keys',
      icon: keyIcon,
      hotkey: '2',
    },
    {
      value: '3',
      label: '3 keys',
      icon: keyIcon,
      hotkey: '3',
    },
    {
      value: '4',
      label: '4 keys',
      icon: keyIcon,
      hotkey: '4',
    },
    {
      value: '5',
      label: '5 keys',
      icon: keyIcon,
      hotkey: '5',
    },
  ];
};

export const NUMBER_OF_KEYS_OPTIONS_MAP = getNumberOfKeysOptions();

export type VehicleNumberOfKeysSelectorProps = {
  id: string;
  value: VehicleListing['num_of_keys'];
  hideCopy?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  withHotkeyListener?: boolean;
  buttonVariant?: 'outline' | 'ghost';
  className?: string;
};

export function VehicleNumberOfKeysSelector({
  id,
  value,
  hideCopy = false,
  side = 'left',
  withHotkeyListener = false,
  buttonVariant = 'ghost',
  className,
}: VehicleNumberOfKeysSelectorProps) {
  const numberOfKeysButtonRef = useRef<HTMLButtonElement>(null);

  const updateNumberOfKeysMutation = useMutation({
    onMutate: async ({ numberOfKeys }) => {
      vehicleListingStore.updateNumberOfKeys({ id, numberOfKeys });
    },
    mutationFn: async ({
      numberOfKeys,
    }: {
      numberOfKeys: VehicleListing['num_of_keys'];
    }) => {
      return await pdkAxios.post(`/inventory/listings/${id}/num_of_keys`, {
        num_of_keys: numberOfKeys,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating number of keys',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleNumberOfKeysChange = (
    newNumberOfKeys: VehicleListing['num_of_keys'],
  ) => {
    updateNumberOfKeysMutation.mutate({ numberOfKeys: newNumberOfKeys });
  };

  const currentOption = useMemo(() => {
    return NUMBER_OF_KEYS_OPTIONS_MAP.find(
      (option) => option.value === value.toString(),
    );
  }, [value]);

  return (
    <div>
      <ComboboxPopover
        endAdornment={
          <KeyboardKeyIcon>
            {HOTKEYS.VEHICLE_LISTING_NUMBER_OF_KEYS}
          </KeyboardKeyIcon>
        }
        placeholder="Set number of keys..."
        currentOption={currentOption}
        onChange={(value) => handleNumberOfKeysChange(parseInt(value))}
        emptyMessage="No number of keys found"
        options={NUMBER_OF_KEYS_OPTIONS_MAP}
        side={side}
        align="start"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = NUMBER_OF_KEYS_OPTIONS_MAP.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handleNumberOfKeysChange(parseInt(option.value));
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div>
          <VehicleNumberOfKeysBadge
            value={value ?? ''}
            hideCopy={hideCopy}
            buttonRef={numberOfKeysButtonRef}
            buttonVariant={buttonVariant}
            className={cn('pl-0', className)}
          />
        </div>
      </ComboboxPopover>
      {withHotkeyListener && (
        <HotKeyListener
          hotkeys={HOTKEYS.VEHICLE_LISTING_NUMBER_OF_KEYS}
          onHotkeyPressed={() => {
            numberOfKeysButtonRef.current?.click();
          }}
        />
      )}
    </div>
  );
}
